import exposeAsGlobal from 'src/services/exposeAsGlobal';

import { extend, Object3DNode, useThree } from '@react-three/fiber';
import CameraControls from 'camera-controls';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import type { Vector3Tuple } from 'three';
import * as THREE from 'three';

import { Theme } from '@mui/material';
import { CoordinatesEditorPanel } from '../EditorTools/CoordinatesEditorPanel';
import { EditorTools } from '../EditorTools/EditorTools';
import { FullControls } from './OnscreenControls/FullControls';
import { HUDWrapper } from './OnscreenControls/HUDWrapper';
import { ResetControls } from './OnscreenControls/ResetControls';
import { useCameraControls } from './useCameraControls';
import { useTransformControls } from './useTransformControls';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      cameraControls: Object3DNode<CameraControls, typeof CameraControls>;
    }
  }
}

CameraControls.install({ THREE });
extend({ CameraControls });

interface IAnimCamera {
  defaultOrbitCenter: Vector3Tuple;
  defaultPosition: Vector3Tuple;
  onCameraHasMovedToNewPositionByProps: Dispatch<SetStateAction<boolean>>;
  theme: Theme;
  fullControls?: boolean;
  applySvtScaling?: boolean;
  sceneMagnitude: number;
  enabledFeatures: string[];
  isResetAtRoot?: boolean;
}

export function AnimCamera({
  defaultOrbitCenter,
  defaultPosition,
  onCameraHasMovedToNewPositionByProps,
  theme,
  fullControls = true,
  applySvtScaling = false, // This prop is maintaining legacy scaling. Will be removed at some point in the future. Hence the default
  sceneMagnitude,
  enabledFeatures,
  isResetAtRoot,
}: IAnimCamera) {
  const { gl, camera } = useThree();

  exposeAsGlobal('camera', camera);

  const { dollySpeed, minZoomLevel, maxZoomLevel } = calculateCameraSettings(
    enabledFeatures,
    applySvtScaling,
    sceneMagnitude
  );

  const {
    transformControlsEnabled,
    pointCoords,
    setPointCoords,
    cameraCoords,
    setCameraCoords,
    inputCoordinates,
    setInputCoordinates,
  } = useTransformControls();

  const { cameraRef, resetCamera } = useCameraControls(
    transformControlsEnabled,
    defaultOrbitCenter,
    defaultPosition,
    camera,
    setCameraCoords,
    onCameraHasMovedToNewPositionByProps
  );

  const [cameraResetAtRoot] = useState(isResetAtRoot);

  useEffect(() => {
    /* istanbul ignore next */
    if (isResetAtRoot) {
      resetCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResetAtRoot, cameraResetAtRoot]);

  /* istanbul ignore next */
  return (
    <>
      <cameraControls
        ref={cameraRef}
        args={[camera, gl.domElement]}
        minDistance={minZoomLevel}
        maxDistance={maxZoomLevel}
        restThreshold={0.1}
        dollySpeed={dollySpeed}
      />
      {cameraRef.current && (
        <HUDWrapper theme={theme}>
          {fullControls ? (
            <FullControls
              cameraControls={cameraRef.current}
              resetCamera={resetCamera}
              minZoomLevel={minZoomLevel}
              maxZoomLevel={maxZoomLevel}
            />
          ) : (
            <ResetControls cameraControls={cameraRef.current} resetCamera={resetCamera} />
          )}
        </HUDWrapper>
      )}

      {transformControlsEnabled && cameraRef.current !== null && (
        <>
          <CoordinatesEditorPanel
            cameraControls={cameraRef.current}
            setInputCoordinates={setInputCoordinates}
            cameraCoords={cameraCoords}
            pointCoords={pointCoords}
          />

          <EditorTools
            cameraControls={cameraRef.current}
            setPointCoords={setPointCoords}
            inputCoordinates={inputCoordinates}
          />
        </>
      )}
    </>
  );
}

export function calculateCameraSettings(enabledFeatures: string[], applySvtScaling: boolean, sceneMagnitude: number) {
  let dollySpeed;
  let minZoomLevel;
  let maxZoomLevel;

  if (!enabledFeatures.includes('configuration-management')) {
    dollySpeed = applySvtScaling ? 1.0 : 0.3;
    minZoomLevel = 0.5;
    maxZoomLevel = applySvtScaling ? 10 : 50;
  } else {
    dollySpeed = sceneMagnitude * 0.2;
    minZoomLevel = sceneMagnitude * 0.05;
    maxZoomLevel = sceneMagnitude * 5;
  }

  return { dollySpeed, minZoomLevel, maxZoomLevel };
}
